import { EOrganization, ESnapshotExists } from 'lib/types';
import AuthActions, {
  authSelector,
  selectShowAllOrgsNotices
} from 'redux/auth';
import NoticeActions from 'redux/notices';
import { AppAsyncThunk, AppThunk } from 'redux/types';
import { NavigateFunction } from 'react-router-dom';
import { ACTIVE_NOTICES_TAB } from 'routes/notices/table/types';
import { MAIN_ROUTES } from 'router/routes';
import Firebase, { signInWithCustomToken } from 'EnoticeFirebase';
import { ResponseOrError, wrapError, wrapSuccess } from 'lib/types/responses';
import { logAndCaptureException } from 'utils';
import { ColumnService } from 'lib/services/directory';
import * as flags from '../../utils/flags';

export function logout(): AppAsyncThunk<ResponseOrError<null>> {
  return async dispatch => {
    try {
      dispatch(AuthActions.startAuth());
      await Firebase.auth().signOut();
      dispatch(AuthActions.logoutSuccess());
      dispatch(AuthActions.endAuth());
      sessionStorage.clear();
      return wrapSuccess(null);
    } catch (error) {
      logAndCaptureException(
        ColumnService.AUTH_AND_USER_MANAGEMENT,
        error,
        'Error logging out'
      );
      dispatch(AuthActions.endAuth());
      return wrapError(new Error('Error logging out'));
    }
  };
}

export function anonymousLogin(): AppAsyncThunk<ResponseOrError<null>> {
  return async (dispatch, getState) => {
    const { userAuth } = authSelector(getState());

    if (!userAuth) {
      try {
        dispatch(AuthActions.startAuth());
        await Firebase.auth().signInAnonymously();
        // Set the LaunchDarkly user to anonymous
        await flags.setUser(undefined);
        // NOTE: endAuth is called in the AuthSaga after the user is set in the store
        return wrapSuccess(null);
      } catch (err) {
        logAndCaptureException(
          ColumnService.AUTH_AND_USER_MANAGEMENT,
          err,
          'Auth: Error in anonymousLogin'
        );
        dispatch(
          AuthActions.setAuthError('Something went wrong in anonymous sign in')
        );
        dispatch(AuthActions.endAuth());
        return wrapError(err as Error);
      }
    }

    return wrapSuccess(null);
  };
}

export function tokenLogin(
  token: string
): AppAsyncThunk<ResponseOrError<null>> {
  return async dispatch => {
    try {
      dispatch(AuthActions.startAuth());
      await Firebase.auth().signOut();
      await signInWithCustomToken(token);
      // NOTE: endAuth is called in the AuthSaga after the user is set in the store
      return wrapSuccess(null);
    } catch (err) {
      logAndCaptureException(
        ColumnService.AUTH_AND_USER_MANAGEMENT,
        err,
        'Auth: Error in tokenLogin'
      );
      dispatch(AuthActions.endAuth());
      return wrapError(err as Error);
    }
  };
}

export function reloadAppState(
  navigate: NavigateFunction,
  searchParams: URLSearchParams
): AppThunk {
  return dispatch => {
    const currentPath = window.location.pathname;
    if (
      currentPath.startsWith(MAIN_ROUTES.NOTICE) ||
      currentPath.startsWith(MAIN_ROUTES.SETTINGS) ||
      currentPath.startsWith(MAIN_ROUTES.AFFIDAVITS)
    ) {
      navigate(MAIN_ROUTES.NOTICES);
      dispatch(NoticeActions.setTab(ACTIVE_NOTICES_TAB));
      dispatch(NoticeActions.setCurrentPage(0));
    } else {
      // Reloads app state without losing auth state
      navigate(`${currentPath}?${searchParams.toString()}`);
    }
  };
}

export function processActiveOrganizationChange(
  newActiveOrg: ESnapshotExists<EOrganization>,
  navigate: NavigateFunction,
  searchParams: URLSearchParams
): AppThunk {
  return (dispatch, getState) => {
    const state = getState();
    if (selectShowAllOrgsNotices(state)) {
      dispatch(AuthActions.showAllOrgsNotices(false));
    }

    // We set the active organization to null before redirecting to the notices table
    // to avoid any listener on other pages that could reset the active organization
    dispatch(AuthActions.setActiveOrganization(null));
    dispatch(AuthActions.setActiveOrganization(newActiveOrg));
    dispatch(reloadAppState(navigate, searchParams));
  };
}
